"use client";

import React, { useEffect } from "react";
import StandardCard from "../common/StandardCard";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { orderActions } from "@/store/actions/order.actions";
import { OrderStatus } from "@/types/models/order";

interface OrderTrackingCardProps {
  orderId: string;
}

const OrderTrackingCard: React.FC<OrderTrackingCardProps> = ({ orderId }) => {
  const dispatch = useAppDispatch();
  const { selectedOrder, detailsLoading, error } = useAppSelector(
    (state) => state.order,
  );

  // No need to fetch here - parent component handles it

  const getTrackingSteps = (orderStatus: OrderStatus) => {
    const baseSteps = [
      { status: "CREATED", label: "Order Created", completed: true },
      { status: "CONFIRMED", label: "Order Confirmed", completed: false },
      { status: "SHIPPING", label: "Shipping", completed: false },
      { status: "COMPLETED", label: "Delivered", completed: false },
    ];

    const statusOrder = [
      "CREATED",
      "CONFIRMED",
      "SHIPPING",
      "COMPLETED",
    ];

    const currentStatusIndex = statusOrder.indexOf(orderStatus);

    return baseSteps.map((step, index) => ({
      ...step,
      completed: index <= currentStatusIndex,
      current: index === currentStatusIndex,
    }));
  };

  if (detailsLoading) {
    return (
      <StandardCard
        title="Order Tracking"
        headerAction={
          <p className="text-xs text-muted-foreground">Loading...</p>
        }
      >
        <div className="space-y-4 animate-pulse">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="h-3 w-3 rounded-full bg-muted"></div>
                {i < 3 && <div className="h-8 w-0.5 bg-muted mt-1"></div>}
              </div>
              <div className="flex-1">
                <div className="h-4 bg-muted rounded w-24 mb-1"></div>
                <div className="h-3 bg-muted rounded w-32"></div>
              </div>
            </div>
          ))}
        </div>
      </StandardCard>
    );
  }

  if (error || !selectedOrder) {
    return (
      <StandardCard
        title="Order Tracking"
        headerAction={<p className="text-xs text-muted-foreground">Error</p>}
      >
        <div className="text-center text-muted-foreground">
          {error || "No tracking information available"}
        </div>
      </StandardCard>
    );
  }

  const trackingSteps = getTrackingSteps(selectedOrder.current_order_stage);
  const lastUpdate = new Date(selectedOrder.updated_at).toLocaleDateString();
  const shippingAddress = selectedOrder.shipping_address;

  return (
    <StandardCard
      title="Order Tracking"
      headerAction={
        <p className="text-xs text-muted-foreground">
          Last Update: {lastUpdate}
        </p>
      }
    >
      <div className="space-y-4">
        {trackingSteps.map((step, index) => {
          const isLast = index === trackingSteps.length - 1;
          const stepDate = step.completed
            ? new Date(selectedOrder.created_at).toLocaleDateString()
            : null;

          return (
            <div key={step.status} className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div
                  className={`h-3 w-3 rounded-full ${
                    step.completed
                      ? step.status === "COMPLETED"
                        ? "bg-green-500"
                        : "bg-primary"
                      : "bg-muted-foreground"
                  }`}
                ></div>
                {!isLast && <div className="h-8 w-0.5 bg-border mt-1"></div>}
              </div>
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    step.completed
                      ? step.status === "COMPLETED"
                        ? "text-green-700 dark:text-green-400"
                        : "text-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {step.label}
                </p>
                {stepDate && (
                  <p className="text-xs text-muted-foreground">{stepDate}</p>
                )}
                {step.status === "SHIPPING" &&
                  shippingAddress &&
                  step.completed && (
                    <p className="text-xs text-muted-foreground">
                      {shippingAddress.city && shippingAddress.state
                        ? `${shippingAddress.city}, ${shippingAddress.state}`
                        : null}
                    </p>
                  )}
              </div>
            </div>
          );
        })}
      </div>
    </StandardCard>
  );
};

export default OrderTrackingCard;
